import {
  add,
  addSeconds,
  Duration,
  isValid,
  isBefore,
  parse,
  formatISO,
  format,
  parseJSON,
  startOfDay,
  setHours,
  setMinutes,
  setSeconds,
  setMilliseconds,
  addMinutes,
  isDate,
  isSameMonth as isSameMonthFromDateFns,
  isSameYear as isSameYearFromDateFns,
  subSeconds,
} from 'date-fns'

import { ptBR } from 'date-fns/locale/pt-BR'
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz'
import { Option } from 'payload'
import { split } from '@utils/misc'
import { MINUTES_IN_SECONDS } from '@utils/constants'

/**
 * Check a date is expired (true if is invalid or before now).
 *
 * @param maybeDate
 * @param formatStr
 * @returns
 */
export function setTime(time: string, date?: Date): Date | null {
  date = date ? new Date(date.getTime()) : new Date();

  const parts = parseTimeString(time);

  if (! parts) {
    return null;
  }

  date = setHours(date, parts.hours);
  date = setMinutes(date, parts.minutes);
  date = setSeconds(date, parts.seconds);
  date = setMilliseconds(date, 0);

  return date;
}

/**
 * Check a date is expired (returns true).
 *
 * Expired dates are in past as we always save the "date to expire" in future (see "expireAt").
 * Invalid dates are expired too.
 *
 * @param maybeDate
 * @param formatStr
 * @param minutes
 * @returns
 */
export function expired(maybeDate: Date | string | null, formatStr: string = 'T', extraSeconds = 0): boolean {
  if (! maybeDate) {
    return true;
  }

  if (! isDate(maybeDate)) {
    maybeDate = parse(String(maybeDate), formatStr, new Date());
  }

  if (! isValid(maybeDate)) {
    return true;
  }

  const expireAt = subSeconds(maybeDate, extraSeconds);

  return isBefore(expireAt, new Date());
}

/**
 * Check "expired" in near minutes (5 by default)
 *
 * @param maybeDate
 * @param minutes
 * @returns
 */
export function almostExpired(maybeDate: Date | string | null, extraMinutes = 5): boolean {
  return expired(maybeDate, 'T', extraMinutes * MINUTES_IN_SECONDS);
}

/**
 * Create a timestamp for seconds on future
 *
 * @param seconds
 * @returns
 */
export function expireAt(seconds: number): string {
  const date = addSeconds(new Date(), seconds);
  return format(date, 'T');
}

/**
 * Parse a date and format it to string
 *
 * @param date
 * @param formatStr
 * @param timezone
 * @param formatOptions
 * @returns
 */
export function toDateString(date: Date | string, formatStr: string = 'yyyy-MM-dd HH:mm:ss', timezone: string | null = 'America/Sao_Paulo', formatOptions = {}): string {
  try {
    if (typeof date === 'string') {
      date = new Date(date);
    }

    return timezone ? formatInTimeZone(date, timezone, formatStr, formatOptions) : format(date, formatStr, formatOptions);
  } catch (error) {
    console.log(error);
  }

  return '';
}

/**
 * Parse a date and format it to string
 *
 * @param date
 * @param formatStr
 * @param timezone
 * @param formatOptions
 * @returns
 */
export function toLocaleDateString(date: Date | string, formatStr: string = 'yyyy-MM-dd HH:mm:ss', timezone: string | null = 'America/Sao_Paulo'): string {
  return toDateString(date, formatStr, timezone, { locale: ptBR });
}

/**
 * Parse a date and format it to ISO string
 *
 * @param date
 * @returns
 */
export function toDateIsoString(date: Date | string): string {
  try {
    if (typeof date === 'string') {
      date = new Date(date);
    }

    return formatISO(date);
  } catch (error) {
    console.log(error);
  }

  return '';
}

/**
 * Parse a date and format it to ISO string
 *
 * @param date
 * @returns
 */
export function toDateDisplayString(date: Date | string): string {
  return toDateString(date, 'dd/MM/yyyy');
}

/**
 * Parse a date and format it to ISO string
 *
 * @param date
 * @returns
 */
export function toDateTimeDisplayString(date: Date | string): string {
  return toDateString(date, 'dd/MM/yyyy HH:mm:ss');
}

/**
 * Parse a date and format it to a comparable (natsort) string
 *
 * @param date
 * @param formatStr
 * @returns
 */
export function toStringComparableDate(date?: Date | string, formatStr: string = 'yyyyMMddHHmmss'): string {
  date = date || new Date();
  return toDateString(date, formatStr);
}

/**
 * Create a list of dates based on a recurrence (duration) with start and end
 *
 * @param start
 * @param end
 * @param recurrence
 * @returns
 */
export function createRecurrenceDates(start: Date, end: Date, recurrence: Duration): Date[] {
  const dates: Date[] = [];

  let nextDate = new Date(start);
  let nextRecurrence = {...recurrence};

  while (nextDate <= end) {
    dates.push(nextDate);
    nextDate = add(start, nextRecurrence);

    for (const key in recurrence) {
      nextRecurrence[key] += recurrence[key];
    }
  }

  return dates;
}

/**
 * Check a date is in current month
 *
 * @param date
 * @param target
 * @returns
 */
export function isSameMonth(date: Date | string, target: Date | string): boolean {
  const start = parseDate(date);
  const end = parseDate(target);

  return isDate(start) && isDate(end) && isSameMonthFromDateFns(start, end);
}

/**
 * Check a date is in current year
 *
 * @param date
 * @param target
 * @returns
 */
export function isSameYear(date: Date, target: Date): boolean {
  const start = parseDate(date);
  const end = parseDate(target);

  return isDate(start) && isDate(end) && isSameYearFromDateFns(start, end);
}

/**
 * A function to check dates are equal
 *
 * @param dateA
 * @param dateB
 * @param includeTime
 * @returns
 */
export function compareDate(dateA: Date, dateB: Date, includeTime: boolean = false): boolean {
  const timeA = includeTime ? dateA.getTime() : startOfDay(dateA).getTime();
  const timeB = includeTime ? dateB.getTime() : startOfDay(dateB).getTime();

  return timeA === timeB;
}

/**
 * A function to check dates are equal
 *
 * @param dateA
 * @param dateB
 * @param includeTime
 * @returns
 */
export function compareDateString(dateA: string, dateB: string, includeTime: boolean = false): boolean {
  if (! includeTime) {
    dateA = dateA.split('T')[0] || '';
    dateB = dateB.split('T')[0] || '';
  }

  return dateA === dateB;
}

/**
 * A function to check a date is in a list of dates
 *
 * @param list
 * @param date
 * @param includeTime
 * @returns
 */
export function includesDate(list: Date[], date: Date, includeTime: boolean = false): boolean {
  for (let i = 0; i < list.length; i++) {
    if (compareDate(list[i], date, includeTime)) {
      return true;
    }
  }

  return false;
}

/**
 * Parse a string to date based on format
 *
 * @param date
 * @param format
 * @param timezone
 * @returns
 */
export function parseDate(date: string | Date, format?: string, timezone: string | null = 'America/Sao_Paulo'): Date | null {
  if (typeof date !== 'string') {
    return date;
  }

  const maybe = format ? parse(date, format, new Date()) : parseJSON(date);
  if (! isValid(maybe)) {
    return null;
  }

  const hasExplicitTimezone = /\d([Zz]|[+-]\d{2}:?\d{2})$/.test(date);
  return (hasExplicitTimezone || ! timezone) ? maybe : fromZonedTime(maybe, timezone);
}

/**
 * Get the half-year period (semester) a date belongs to: 1st (Jan-Jun) or 2nd (Jul-Dec).
 * Boundaries are computed in the given timezone, so "end" is always the last day/hour of June or December.
 *
 * @param date
 * @param timezone
 * @returns
 */
export type Semester = { key: string, start: Date, end: Date };
export function getCurrentSemester(date: Date | string = new Date(), timezone: string | null = 'America/Sao_Paulo'): Semester {
  const target = (typeof date === 'string' ? parseDate(date, undefined, timezone) : date) as Date;

  const year = parseInt(toDateString(target, 'yyyy', timezone), 10);
  const month = parseInt(toDateString(target, 'M', timezone), 10);

  const half: 1 | 2 = month <= 6 ? 1 : 2;
  const key = `${year}-${half}`;

  const startWallClock = half === 1 ? `01/01/${year} 00:00:00.000` : `01/07/${year} 00:00:00.000`;
  const endWallClock = half === 1 ? `30/06/${year} 23:59:59.999` : `31/12/${year} 23:59:59.999`;

  const startNaive = parse(startWallClock, 'dd/MM/yyyy HH:mm:ss.SSS', new Date());
  const endNaive = parse(endWallClock, 'dd/MM/yyyy HH:mm:ss.SSS', new Date());

  return {
    key,
    start: timezone ? fromZonedTime(startNaive, timezone) : startNaive,
    end: timezone ? fromZonedTime(endNaive, timezone) : endNaive,
  };
}

/**
 * Try to discover a format and parse a string to date
 *
 * @param date
 * @param format
 * @param timezone
 * @returns
 */
export function extractDate(text: string): Date | null {
  const parts = split(text, false);
  const dates = (parts || []).map(part => {
    part = part.trim();

    const formats = [
      'yyyyMMdd', 'yyyyMdd', 'yyyyMd',
      'dd.MM.yy', 'dd.MM.yyyy', 'd.M.yy', 'd.M.yyyy',
      'dd-MM-yy', 'dd-MM-yyyy', 'd-M-yy', 'd-M-yyyy',
    ];

    for (let i = 0; i < formats.length; i++) {
      const maybe = parse(part, formats[i], new Date());
      if (isValid(maybe)) {
        return maybe;
      }
    }

    return null
  });

  return dates.filter(x => x && isValid(x))[0] || null;
}

/**
 * Parse time info from a string
 *
 * @param date
 * @param format
 * @param timezone
 * @returns
 */
export type TimeStringParsed = { hours: number, minutes: number, seconds: number, formated: { hours: string, minutes: string, seconds: string, hm: string, hms: string } };
export function parseTimeString(time: string): TimeStringParsed | null {
  const parts = time.split(':');

  if (! parts) {
    return null;
  }

  const hours = parseInt(parts[0], 10);
  const minutes = parseInt(parts[1] || '0', 10);
  const seconds = parseInt(parts[2] || '0', 10);

  return {
    hours,
    minutes,
    seconds,
    formated: {
      hours: hours.toString().padStart(2, '0'),
      minutes: minutes.toString().padStart(2, '0'),
      seconds: seconds.toString().padStart(2, '0'),
      hm: `${ hours.toString().padStart(2, '0') }:${ minutes.toString().padStart(2, '0') }`,
      hms: `${ hours.toString().padStart(2, '0') }:${ minutes.toString().padStart(2, '0') }:${ seconds.toString().padStart(2, '0') }`,
    }
  };
}

/**
 * Create a array of options for a time range
 *
 * @param start
 * @param end
 * @param timeIntervals
 * @param displayFormat
 * @returns
 */
export function createOptionsForTimeRange(startTime: string, endTime: string, interval: number, displayFormat: string = 'HH:mm:ss'): Option[] {
  const start = parseTimeString(startTime || '');
  const end = parseTimeString(endTime || '');

  if (! start || ! end) {
    return [];
  }

  const options: Option[] = [];

  let currentDate = setTime(start.formated.hms, new Date()) as Date;
  const endDate = setTime(end.formated.hms, new Date()) as Date;

  while (isBefore(currentDate, endDate) || compareDate(currentDate, endDate, true)) {
    options.push({
      label: toDateString(currentDate, displayFormat),
      value: toDateString(currentDate, 'HH:mm:ss'),
    });

    currentDate = addMinutes(currentDate, interval);
  }

  return options;
};
